import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Trash2, RefreshCw, Calendar, Filter } from 'lucide-react';

function api(method, url, data = null, config = {}) {
  const pw = sessionStorage.getItem('admin_password');
  return axios({ method, url, data, ...config, headers: { 'x-adminpassword': pw } });
}

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (dateFilter) params.date = dateFilter;
    api('get', '/api/admin/bookings', null, { params })
      .then(r => {
        // Ordena por data (mais recente primeiro) e depois por horário
        const sorted = r.data.sort((a, b) => {
          const dateA = new Date(a.booking_date + 'T' + a.booking_time);
          const dateB = new Date(b.booking_date + 'T' + b.booking_time);
          return dateA - dateB;
        });
        setBookings(sorted);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dateFilter]);

  useEffect(() => { load(); }, [load]);

  const cancel = async (id) => {
    if (!window.confirm('Cancelar esta reserva?')) return;
    await api('delete', `/api/admin/bookings/${id}`);
    load();
  };

  // Filtra por status
  const filteredBookings = bookings.filter(b => {
    if (statusFilter === 'all') return true;
    return b.status === statusFilter;
  });

  // Agrupa por data
  const groupedByDate = filteredBookings.reduce((acc, booking) => {
    const date = booking.booking_date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(booking);
    return acc;
  }, {});

  // Ordena as datas
  const sortedDates = Object.keys(groupedByDate).sort();

  const formatDate = (d) => {
    if (!d) return '';
    const [y, m, day] = d.split('-');
    return `${day}/${m}/${y}`;
  };

  const formatDateLong = (d) => {
    if (!d) return '';
    const [y, m, day] = d.split('-');
    const date = new Date(y, m - 1, day);
    const weekDays = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
    const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
    return `${weekDays[date.getDay()]}, ${day} de ${monthNames[date.getMonth()]} de ${y}`;
  };

  return (
    <div className="admin-section">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <h2 className="admin-section-title" style={{ marginBottom: 0 }}>Agendamentos</h2>
        <button className="btn-primary" style={{ fontSize: '0.85rem', padding: '0.6rem 1.2rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }} onClick={load}>
          <RefreshCw size={15} /> Atualizar
        </button>
      </div>

      <div className="admin-filters">
        <Filter size={16} color="var(--text-gray)" />
        <input
          type="date"
          value={dateFilter}
          onChange={e => setDateFilter(e.target.value)}
          title="Filtrar por data"
        />
        <select 
          value={statusFilter} 
          onChange={e => setStatusFilter(e.target.value)}
          style={{ padding: '0.5rem', borderRadius: '8px', border: '1px solid #ddd' }}
        >
          <option value="all">Todos os status</option>
          <option value="confirmed">Confirmados</option>
          <option value="cancelled">Cancelados</option>
        </select>
        {(dateFilter || statusFilter !== 'all') && (
          <button className="btn-secondary" style={{ fontSize: '0.8rem', padding: '0.45rem 1rem' }} onClick={() => { setDateFilter(''); setStatusFilter('all'); }}>
            Limpar filtros
          </button>
        )}
        <span style={{ fontSize: '0.82rem', color: 'var(--text-gray)', marginLeft: 'auto' }}>
          {filteredBookings.length} reserva(s)
        </span>
      </div>

      <div className="admin-table-wrap">
        {loading ? (
          <div className="empty-state">Carregando...</div>
        ) : filteredBookings.length === 0 ? (
          <div className="empty-state">
            <Calendar size={40} strokeWidth={1} style={{ color: 'var(--gold-light)', marginBottom: '0.75rem' }} />
            <p>Nenhum agendamento encontrado.</p>
          </div>
        ) : (
          <div className="bookings-list">
            {sortedDates.map(date => (
              <div key={date} className="booking-date-group">
                <div className="booking-date-header">
                  <Calendar size={16} />
                  <span>{formatDateLong(date)}</span>
                  <span className="booking-count">{groupedByDate[date].length} agendamento(s)</span>
                </div>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Horário</th>
                      <th>Cliente</th>
                      <th>Telefone</th>
                      <th>Profissional</th>
                      <th>Serviço</th>
                      <th>Valor</th>
                      <th>Status</th>
                      <th>Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedByDate[date].map(b => (
                      <tr key={b.id}>
                        <td>
                          <span style={{ fontWeight: 600, color: 'var(--dark)' }}>
                            {b.booking_time}
                          </span>
                        </td>
                        <td><strong>{b.client_name}</strong></td>
                        <td>{b.client_phone}</td>
                        <td>{b.stylist_name}</td>
                        <td>{b.service_name}</td>
                        <td style={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, color: 'var(--gold-dark)' }}>
                          R$ {b.price?.toFixed(2).replace('.', ',')}
                        </td>
                        <td>
                          <span className={`badge ${b.status === 'confirmed' ? 'badge-confirmed' : 'badge-cancelled'}`}>
                            {b.status === 'confirmed' ? 'Confirmado' : 'Cancelado'}
                          </span>
                        </td>
                        <td>
                          {b.status === 'confirmed' && (
                            <button className="btn-icon" title="Cancelar reserva" onClick={() => cancel(b.id)}>
                              <Trash2 size={15} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
